import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import { ApiError, api, formValues, type BillingConfig, type Client, type Contract, type ContractInput, type Employee, type EverhourCheckResult } from '../api.js';
import { navigate } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/audit-dialog.js';
import type { AuditDialog } from '../components/audit-dialog.js';

@customElement('contracts-page')
export class ContractsPage extends LitElement {
  static styles = pageStyles;

  @state() private items: Contract[] = [];
  @state() private employees: Employee[] = [];
  @state() private clients: Client[] = [];
  @state() private docCounts: Record<string, number> = {};
  @state() private loading = true;
  @state() private error = '';

  @state() private dialogOpen = false;
  @state() private editing: Contract | null = null;
  @state() private kind: 'hourly' | 'guaranteed' = 'hourly';
  @state() private saving = false;
  @state() private formError = '';
  @state() private fieldErrors: Record<string, string> = {};
  // Everhour "Test" button state.
  @state() private selectedClientId = '';
  // Billing groups of the selected client, for the default-group picker.
  @state() private clientGroups: { id: number; name: string }[] = [];
  @state() private everhourResult: EverhourCheckResult | null = null;
  @state() private everhourError = '';
  @state() private everhourTesting = false;

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = '';
    try {
      [this.items, this.employees, this.clients, this.docCounts] = await Promise.all([
        api.get<Contract[]>('/api/contracts'),
        api.get<Employee[]>('/api/employees'),
        api.get<Client[]>('/api/clients'),
        api.documentCounts('contract'),
      ]);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private openNew() {
    this.editing = null;
    this.kind = 'hourly';
    this.selectedClientId = '';
    this.clientGroups = [];
    this.resetFormState();
    this.dialogOpen = true;
  }

  private openEdit(item: Contract) {
    this.editing = item;
    this.kind = item.kind;
    this.selectedClientId = String(item.client_id);
    void this.loadGroups(item.client_id);
    this.resetFormState();
    this.dialogOpen = true;
  }

  private history(id: number, label: string) {
    void (this.renderRoot.querySelector('audit-dialog') as AuditDialog).show('contract', id, label);
  }

  private resetFormState() {
    this.formError = '';
    this.fieldErrors = {};
    this.saving = false;
    this.everhourResult = null;
    this.everhourError = '';
    this.everhourTesting = false;
  }

  private async loadGroups(clientId: number) {
    try {
      const cfg = await api.get<BillingConfig>(`/api/clients/${clientId}/billing`);
      this.clientGroups = cfg.groups.filter((g) => g.id !== null).map((g) => ({ id: g.id as number, name: g.name }));
    } catch {
      this.clientGroups = [];
    }
  }

  private onClientChange(e: Event) {
    this.selectedClientId = (e.target as HTMLSelectElement).value;
    this.clientGroups = [];
    if (this.selectedClientId) void this.loadGroups(Number(this.selectedClientId));
  }

  private contractForm(): HTMLFormElement {
    return this.renderRoot.querySelector('#contract-form') as HTMLFormElement;
  }

  /** Calls Everhour with the chosen client's token and shows who the ID points at. */
  private async testEverhour() {
    const v = formValues(this.contractForm());
    this.everhourResult = null;
    this.everhourError = '';
    if (!v.client_id) {
      this.everhourError = 'Choose a client first; its Everhour API token is used for the test.';
      return;
    }
    this.everhourTesting = true;
    try {
      this.everhourResult = await api.post<EverhourCheckResult>('/api/contracts/everhour-check', {
        client_id: Number(v.client_id),
        employee_id: v.employee_id ? Number(v.employee_id) : null,
        everhour_user_id: v.everhour_user_id,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = Object.values(err.fields)[0];
        this.everhourError = hint ? `${err.message}: ${hint}` : err.message;
      } else {
        this.everhourError = (err as Error).message;
      }
    } finally {
      this.everhourTesting = false;
    }
  }

  /** Fills the Everhour user ID from a candidate row. */
  private useCandidate(id: number) {
    const input = this.contractForm().querySelector('wa-input[name="everhour_user_id"]') as (HTMLElement & { value: string }) | null;
    if (input) input.value = String(id);
    this.everhourResult = null;
    this.everhourError = '';
  }

  private async save(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    const input: ContractInput = {
      employee_id: Number(v.employee_id ?? 0),
      client_id: Number(v.client_id ?? 0),
      period: { start: v.start ?? '', end: v.end },
      hourly_rate: v.hourly_rate ?? '',
      currency: v.currency ?? 'EUR',
      kind: v.kind ?? 'hourly',
      guaranteed_hours_per_month: v.kind === 'guaranteed' ? v.guaranteed_hours_per_month : null,
      everhour_user_id: v.everhour_user_id,
      default_billing_group_id: v.default_billing_group_id ? Number(v.default_billing_group_id) : null,
      billing_group_mode: v.billing_group_mode !== undefined && v.billing_group_mode !== null ? 'always' : 'fallback',
    };
    this.saving = true;
    this.formError = '';
    this.fieldErrors = {};
    try {
      if (this.editing) {
        await api.put(`/api/contracts/${this.editing.id}`, input);
      } else {
        await api.post('/api/contracts', input);
      }
      this.dialogOpen = false;
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        this.formError = err.message;
        this.fieldErrors = err.fields;
      } else {
        this.formError = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  private async deleteItem(item: Contract) {
    if (!confirm(`Delete the contract of ${item.employee_name} with ${item.client_name}?`)) return;
    try {
      await api.delete(`/api/contracts/${item.id}`);
      await this.load();
    } catch (err) {
      this.error = (err as Error).message;
    }
  }

  render() {
    const canCreate = this.employees.length > 0 && this.clients.length > 0;
    return html`
      <div class="page-header">
        <h2>Contracts</h2>
        <span class="spacer"></span>
        <wa-button variant="brand" ?disabled=${!canCreate} @click=${this.openNew}>New contract</wa-button>
      </div>
      ${!this.loading && !canCreate
        ? html`<wa-callout variant="neutral">Add at least one employee and one client before creating contracts.</wa-callout>`
        : nothing}
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.renderTable()} ${this.renderDialog()}
      <audit-dialog></audit-dialog>
    `;
  }

  private renderTable() {
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    if (this.items.length === 0) return html`<div class="empty">No contracts yet.</div>`;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Developer</th>
            <th>Client</th>
            <th>From</th>
            <th>To</th>
            <th>Kind</th>
            <th>Rate</th>
            <th>Guaranteed h/month</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${this.items.map(
            (c) => html`
              <tr>
                <td data-label="Developer">
                  ${c.employee_name}
                  ${c.everhour_user_id ? html`<span class="sub">Everhour: ${c.everhour_user_id}</span>` : nothing}
                </td>
                <td data-label="Client">${c.client_name}</td>
                <td class="date" data-label="From">${c.period.start}</td>
                <td class="date" data-label="To">${c.period.end ?? html`<span class="muted">open</span>`}</td>
                <td data-label="Kind">
                  ${c.kind === 'guaranteed'
                    ? html`<wa-tag size="small" variant="brand">guaranteed</wa-tag>`
                    : html`<wa-tag size="small" variant="neutral">hourly</wa-tag>`}
                </td>
                <td class="num" data-label="Rate">${c.hourly_rate} ${c.currency}</td>
                <td class=${c.guaranteed_hours_per_month ? 'num' : 'num empty-cell'} data-label="Guaranteed">${c.guaranteed_hours_per_month ?? html`<span class="muted">—</span>`}</td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => navigate(`/documents?contract_id=${c.id}`)}>Documents (${this.docCounts[c.id] ?? 0})</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.history(c.id, `${c.employee_name} @ ${c.client_name}`)}>History</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.openEdit(c)}>Edit</wa-button>
                  <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.deleteItem(c)}>Delete</wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }

  private renderEverhourResult() {
    if (this.everhourError) {
      return html`<wa-callout class="full" variant="danger" size="small">${this.everhourError}</wa-callout>`;
    }
    const r = this.everhourResult;
    if (!r) return nothing;
    const employee = this.employees.find((e) => String(e.id) === formValues(this.contractForm()).employee_id);
    const m = r.member;
    let variant: 'success' | 'warning' | 'neutral' = 'neutral';
    if (m) variant = m.status === 'active' && m.email_matches_employee !== false ? 'success' : 'warning';
    return html`
      <wa-callout class="full" variant=${variant} size="small">
        <div class="check-lines">
          <div>Workspace <strong>${r.workspace.name}</strong> · key owned by ${r.workspace.key_owner} (${r.workspace.key_owner_email})</div>
          ${m
            ? html`
                <div>
                  Member <strong>${m.id}</strong> · ${m.name} · ${m.email} · ${m.status} ${m.role}${m.headline ? ` · ${m.headline}` : ''}
                  ${m.email_matches_employee === true ? html` · <strong>matches the employee's email</strong>` : nothing}
                  ${m.email_matches_employee === false ? html` · <strong>email differs from ${employee?.name ?? 'the employee'}'s ${employee?.email ?? ''}</strong>` : nothing}
                </div>
                ${r.activity
                  ? html`<div>
                      Last ${activityDaysLabel(r.activity.from, r.activity.to)}:
                      ${r.activity.entries === 0
                        ? 'no time tracked'
                        : html`<strong>${r.activity.hours} h</strong> in ${r.activity.entries} entries, last on ${r.activity.last_entry}`}
                    </div>`
                  : nothing}
              `
            : html`
                <div>${formValues(this.contractForm()).everhour_user_id ? 'No member with that ID.' : 'No ID entered.'} Pick one of the ${r.candidates.length} workspace members:</div>
                <div class="candidates">
                  ${r.candidates.map(
                    (cand) => html`
                      <div class="row">
                        <span class="grow">${cand.name} · ${cand.email} · <span class="mono">${cand.id}</span>${cand.status !== 'active' ? ` · ${cand.status}` : ''}</span>
                        <wa-button size="small" appearance="plain" @click=${() => this.useCandidate(cand.id)}>Use</wa-button>
                      </div>
                    `,
                  )}
                </div>
              `}
        </div>
      </wa-callout>
    `;
  }

  // wa-select and other children emit the same bubbling wa-after-hide when
  // their dropdown closes; only the dialog's own event should close it.
  private onDialogHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.dialogOpen = false;
  };

  private renderDialog() {
    const c = this.editing;
    const err = (f: string) => this.fieldErrors[f] ?? '';
    return html`
      <wa-dialog
        label=${c ? 'Edit contract' : 'New contract'}
        ?open=${this.dialogOpen}
        @wa-after-hide=${this.onDialogHidden}

        style="--width: min(45rem, calc(100vw - 1.5rem));"
      >
        <form id="contract-form" @submit=${this.save}>
          <wa-select name="employee_id" label="Developer" required .value=${String(c?.employee_id ?? '')} hint=${err('employee_id')}>
            ${this.employees.map((e) => html`<wa-option value=${String(e.id)}>${e.name}</wa-option>`)}
          </wa-select>
          <wa-select
            name="client_id"
            label="Client"
            required
            .value=${String(c?.client_id ?? '')}
            @change=${this.onClientChange}
            hint=${err('client_id')}
          >
            ${this.clients.map((cl) => html`<wa-option value=${String(cl.id)}>${cl.name}</wa-option>`)}
          </wa-select>
          <wa-input name="start" label="From" type="date" required .value=${c?.period.start ?? ''} hint=${err('period')}></wa-input>
          <wa-input name="end" label="To (leave empty for open-ended)" type="date" .value=${c?.period.end ?? ''}></wa-input>
          <wa-input name="hourly_rate" label="Hourly rate" inputmode="decimal" required placeholder="30.00" .value=${c?.hourly_rate ?? ''} hint=${err('hourly_rate')}></wa-input>
          <wa-select name="currency" label="Currency" .value=${c?.currency ?? 'EUR'} hint=${err('currency')}>
            <wa-option value="EUR">EUR</wa-option>
            <wa-option value="MKD">MKD</wa-option>
          </wa-select>
          <wa-select
            name="kind"
            label="Kind"
            .value=${this.kind}
            @change=${(e: Event) => (this.kind = (e.target as HTMLSelectElement).value as 'hourly' | 'guaranteed')}
            hint=${err('kind')}
          >
            <wa-option value="hourly">Hourly</wa-option>
            <wa-option value="guaranteed">Guaranteed</wa-option>
          </wa-select>
          <wa-input
            name="guaranteed_hours_per_month"
            label="Guaranteed hours per month"
            inputmode="decimal"
            placeholder="158"
            ?disabled=${this.kind !== 'guaranteed'}
            .value=${c?.guaranteed_hours_per_month ?? ''}
            hint=${err('guaranteed_hours_per_month') || 'Invoiced as hours × rate plus an adjustment line'}
          ></wa-input>
          <div class="full with-button">
            <wa-input
              name="everhour_user_id"
              label="Everhour user ID"
              .value=${c?.everhour_user_id ?? ''}
              hint=${err('everhour_user_id') || "Optional; the developer's ID in this client's Everhour workspace, used by the time import"}
            ></wa-input>
            <wa-button
              appearance="outlined"
              ?disabled=${!this.selectedClientId}
              ?loading=${this.everhourTesting}
              title=${this.selectedClientId ? "Check the client's Everhour token and look up this ID" : 'Choose a client first'}
              @click=${this.testEverhour}
              >Test</wa-button
            >
          </div>
          ${this.renderEverhourResult()}
          <wa-select name="default_billing_group_id" label="Default billing group" with-clear .value=${String(c?.default_billing_group_id ?? '')} hint=${err('default_billing_group_id') || (this.clientGroups.length ? 'Used when no routing rule matches (Billable/Internal and GitHub tasks)' : 'The client has no billing groups yet')}>
            ${this.clientGroups.map((g) => html`<wa-option value=${String(g.id)}>${g.name}</wa-option>`)}
          </wa-select>
          <wa-switch name="billing_group_mode" ?checked=${c?.billing_group_mode === 'always'} hint="Route every entry of this developer to the default group, ignoring rules">Always use the default group</wa-switch>
          ${this.formError ? html`<wa-callout class="form-error" variant="danger">${this.formError}</wa-callout>` : nothing}
        </form>
        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.dialogOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" form="contract-form" ?loading=${this.saving}>Save</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

/** "30 days (2026-09-14 – 2026-10-13)" */
function activityDaysLabel(from: string, to: string): string {
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
  return `${days} days (${from} – ${to})`;
}

declare global {
  interface HTMLElementTagNameMap {
    'contracts-page': ContractsPage;
  }
}
